// ticket-dag-integration-slice-rule.ts
//
// V2 spec §4.3 (V2-3), DAG rule 5 — warning only, never a reject (a real
// planner would loop; G2 already puts a person in front of every plan). A plan
// with ≥ 2 FEATURE slices needs one ticket in slice `integration` that is
// kind `test`, depends on every `test` ticket of those feature slices, and has
// a non-blank `verification_recipe`. Plan with 1 feature slice ⇒ nothing.
// "Depends on" = the transitive `depends_on` closure (review M3): an
// integration slice testcase → implement → test whose testcase depends on the
// feature tests satisfies the rule through its test ticket.
//
// "Feature slice" = a slice (other than `integration`) holding at least one
// `implement` ticket. That leaves out bootstrap survey/plan tickets, the
// controller's redirect decision tickets (slice `directive`) and the release
// review tickets (slice `release`) without listing them one by one.
// Pure: no I/O. Read-only for G2 — the plan hash is computed elsewhere (§4.3 (4)).
import type { Ticket } from "./schemas/ticket-schema.js";

export const INTEGRATION_SLICE_ID = "integration";

/** Fixed phrase the G2 reason / `run` terminal warning / tests look for (E5). */
const MISSING_INTEGRATION = "thiếu lát integration";

/** Every ticket id reachable from `startId` through `depends_on` (visited set ⇒ safe even on a cycle). */
function dependencyClosure(startId: string, byId: ReadonlyMap<string, Ticket>): Set<string> {
  const seen = new Set<string>();
  const stack = [...(byId.get(startId)?.depends_on ?? [])];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (seen.has(id)) continue;
    seen.add(id);
    stack.push(...(byId.get(id)?.depends_on ?? []));
  }
  return seen;
}

export function findMissingIntegrationSliceWarning(tickets: readonly Ticket[]): string | undefined {
  const featureSlices = [
    ...new Set(
      tickets
        .filter((t) => t.kind === "implement" && t.slice_id !== INTEGRATION_SLICE_ID)
        .map((t) => t.slice_id),
    ),
  ];
  if (featureSlices.length < 2) return undefined;

  const featureTestIds = tickets
    .filter((t) => t.kind === "test" && featureSlices.includes(t.slice_id))
    .map((t) => t.id);
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const satisfied = tickets.some((t) => {
    if (t.slice_id !== INTEGRATION_SLICE_ID || t.kind !== "test") return false;
    if (t.verification_recipe.trim() === "") return false;
    const reachable = dependencyClosure(t.id, byId);
    return featureTestIds.every((id) => reachable.has(id));
  });
  if (satisfied) return undefined;
  return (
    `${MISSING_INTEGRATION}: plan có ${featureSlices.length} slice (${featureSlices.join(", ")}) ` +
    `nhưng không có ticket slice_id "${INTEGRATION_SLICE_ID}", kind "test", depends_on (trực tiếp/bắc cầu) mọi ticket test ` +
    `của các slice đó (${featureTestIds.join(", ") || "—"}) và có verification_recipe`
  );
}
